"use client";

import { Link } from "@/i18n/navigation";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import React from "react";

const buttonVariants = cva(
  // Base styles
  [
    "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200",
    "disabled:cursor-not-allowed disabled:opacity-100",
    "active:scale-95",
    "cursor-pointer",
  ],
  {
    variants: {
      color: {
        primary: "",
        danger: "",
        white: "",
        purple: "",
        black: "",
      },
      variant: {
        solid: "",
        outline: "",
        ghost: "",
        link: "",
      },
      size: {
        xxs: ["h-auto py-[6px] px-[12px] text-[13px]", "gap-2"],
        xs: ["h-9 py-0 px-4 text-[14px]", "gap-2"],
        sm: ["h-10 py-0 px-4 text-[14px]", "gap-2"],
        default: ["h-11 py-0 px-6 text-[16px]", "gap-2"],
        lg: ["h-[50px] py-0 px-6 text-[16px]", "gap-2"],
        xl: ["h-14 py-0 px-6 text-[16px]", "gap-2"],
        giant: ["h-[60px] py-0 px-6 text-[18px]", "gap-2"],
      },
    },
    compoundVariants: [
      // Primary variants
      {
        color: "primary",
        variant: "solid",
        class: [
          "bg-primary text-white hover:bg-primary-emphasis",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
          "shadow-soft hover:shadow-medium",
          "disabled:bg-primary/30 disabled:text-white",
        ],
      },
      {
        color: "primary",
        variant: "outline",
        class: [
          "border border-primary bg-transparent text-primary",
          "hover:bg-primary hover:text-white",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
          "disabled:text-foreground-on-disabled disabled:border-foreground-disabled",
        ],
      },
      {
        color: "primary",
        variant: "ghost",
        class: [
          "bg-white text-primary hover:bg-primary hover:text-white",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
          "disabled:opacity-50",
        ],
      },
      {
        color: "primary",
        variant: "link",
        class: [
          "!h-auto !p-0",
          "bg-transparent text-primary underline-offset-4",
          "hover:underline hover:text-primary-emphasis",
          "focus:outline-none",
          "disabled:opacity-50",
        ],
      },
      // Danger variants
      {
        color: "danger",
        variant: "solid",
        class: [
          "bg-danger text-white hover:bg-danger-emphasis",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger",
          "shadow-soft hover:shadow-medium",
          "disabled:bg-foreground-disabled disabled:text-foreground-on-disabled",
        ],
      },
      {
        color: "danger",
        variant: "outline",
        class: [
          "border border-danger bg-transparent text-danger",
          "hover:bg-danger hover:text-white",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger",
          "disabled:text-foreground-on-disabled disabled:border-foreground-disabled",
        ],
      },
      {
        color: "danger",
        variant: "ghost",
        class: [
          "bg-white text-danger hover:bg-danger hover:text-white",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger",
          "disabled:opacity-50",
        ],
      },
      {
        color: "danger",
        variant: "link",
        class: [
          "!h-auto !p-0",
          "bg-transparent text-danger underline-offset-4",
          "hover:underline hover:text-danger-emphasis",
          "focus:outline-none",
          "disabled:opacity-50",
        ],
      },
      // White variants
      {
        color: "white",
        variant: "solid",
        class: [
          "bg-white text-foreground hover:bg-foreground-muted",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-foreground",
          "shadow-soft hover:shadow-medium",
          "disabled:bg-foreground-disabled disabled:text-foreground-on-disabled",
        ],
      },
      {
        color: "white",
        variant: "outline",
        class: [
          "border border-white bg-transparent text-white",
          "hover:bg-white hover:text-foreground",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-foreground",
          "disabled:text-foreground-on-disabled disabled:border-foreground-disabled",
        ],
      },
      {
        color: "white",
        variant: "ghost",
        class: [
          "text-white hover:bg-white hover:text-foreground",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-foreground",
          "disabled:opacity-50",
        ],
      },
      {
        color: "white",
        variant: "link",
        class: [
          "!h-auto !p-0",
          "bg-transparent text-white underline-offset-4",
          "hover:underline hover:text-foreground-muted",
          "focus:outline-none",
          "disabled:opacity-50",
        ],
      },
      // Purple variants
      {
        color: "purple",
        variant: "solid",
        class: [
          "bg-purple text-white hover:bg-primary-emphasis",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple",
          "shadow-soft hover:shadow-medium",
          "disabled:bg-foreground-disabled disabled:text-foreground-on-disabled",
        ],
      },
      {
        color: "purple",
        variant: "outline",
        class: [
          "border border-purple bg-transparent text-purple",
          "hover:bg-purple hover:text-white",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple",
          "disabled:text-foreground-on-disabled disabled:border-foreground-disabled",
        ],
      },
      {
        color: "purple",
        variant: "ghost",
        class: [
          "bg-white text-purple hover:bg-purple hover:text-white",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple",
          "disabled:opacity-50",
        ],
      },
      {
        color: "purple",
        variant: "link",
        class: [
          "!h-auto !p-0",
          "bg-transparent text-purple underline-offset-4",
          "hover:underline hover:text-primary-emphasis",
          "focus:outline-none",
          "disabled:opacity-50",
        ],
      },
      // Black variants
      {
        color: "black",
        variant: "solid",
        class: [
          "bg-foreground text-white hover:bg-foreground-muted",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-foreground",
          "shadow-soft hover:shadow-medium",
          "disabled:bg-foreground-disabled disabled:text-foreground-on-disabled",
        ],
      },
      {
        color: "black",
        variant: "outline",
        class: [
          "border border-foreground bg-transparent text-foreground",
          "hover:bg-foreground hover:text-white",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-foreground",
          "disabled:text-foreground-on-disabled disabled:border-foreground-disabled",
        ],
      },
      {
        color: "black",
        variant: "ghost",
        class: [
          "bg-white text-foreground hover:bg-foreground hover:text-white",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-foreground",
          "disabled:opacity-50",
        ],
      },
      {
        color: "black",
        variant: "link",
        class: [
          "!h-auto !p-0",
          "bg-transparent text-foreground underline-offset-4",
          "hover:underline hover:text-foreground-muted",
          "focus:outline-none",
          "disabled:opacity-50",
        ],
      },

      // Default variants
      {
        color: "default",
        variant: "outline",
        class: [
          "border border-border bg-transparent text-foreground",
          "hover:bg-background-200 hover:border-border-strong",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-foreground",
        ],
      },
      {
        color: "default",
        variant: "ghost",
        class: [
          "bg-transparent text-foreground hover:bg-background-200",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-foreground",
        ],
      },
    ],
    defaultVariants: {
      color: "primary",
      variant: "solid",
      size: "default",
    },
  },
);

const Button = React.forwardRef(
  (
    {
      className,
      color,
      variant,
      size,
      type = "button",
      children,
      leftIcon,
      rightIcon,
      loading = false,
      disabled,
      href,
      target = "_self",
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    // Icon size mapping (tailwind class)
    let iconClass = "h-6 w-6";
    if (size === "xxs") iconClass = "h-4 w-4";
    else if (size === "xs" || size === "sm") iconClass = "h-5 w-5";

    // Helper to clone icon with tailwind class
    const withIconSize = (icon) => {
      if (!icon) return null;
      const prevClass = icon.props?.className || "";
      return React.cloneElement(icon, {
        className: `${iconClass} ${prevClass}`.trim(),
      });
    };

    const content = (
      <>
        {withIconSize(leftIcon)}
        {children}
        {withIconSize(rightIcon)}
        {loading && (
          <svg
            className="h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
      </>
    );

    const buttonClasses = clsx(
      buttonVariants({ color, variant, size }),
      className,
      isDisabled && "cursor-not-allowed",
      loading && !disabled && "cursor-progress",
    );

    // useEffect(() => {
    //   window.dispatchEvent(new Event("resize"));
    // }, [loading]);

    if (href) {
      return (
        <Link
          href={href}
          disabled={isDisabled}
          target={target}
          className={buttonClasses}
          ref={ref}
          {...props}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        type={type}
        className={buttonClasses}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {content}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
